/**
 * Copyright 2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useCloudAccountCostsByChargeTypeQuery } from "~/generated/graphql";
import { Maybe, isNotNil } from "~/tools";
import {
  CloudProviderType,
  Provider,
  ProviderTab,
  ProvidersForFiltering,
} from "../../types";
import {
  getTotalCost,
  isKubernetesOrOpenshiftTab,
  getAccountStatusFilter,
} from "../../utils";

type Props = {
  query: Maybe<string>;
  status: string[];
  provider: ProviderTab;
  providerFilter: ProvidersForFiltering[];
  isStatusExcluded?: true;
  isProviderExcluded?: true;
};

export function useK8sOcTotalsByProvider({
  query,
  status,
  provider,
  providerFilter,
  isStatusExcluded,
  isProviderExcluded,
}: Props) {
  const shouldSkip = !isKubernetesOrOpenshiftTab(provider);
  const providerType =
    provider === ProviderTab.KUBERNETES
      ? CloudProviderType.Kubernetes
      : CloudProviderType.Openshift;

  const commonFilters = {
    ...getAccountStatusFilter(status),
    isStatusExcluded,
    isProviderExcluded,
  };

  const { data: EKSCost } = useCloudAccountCostsByChargeTypeQuery({
    skip:
      shouldSkip ||
      (isNotNil(providerFilter) && !providerFilter.includes(Provider.Aws)),
    variables: {
      query: query?.toString(),
      filter: {
        ...commonFilters,
        provider: [Provider.Aws],
        providerType: [providerType],
      },
    },
  });
  const { data: AKSCost } = useCloudAccountCostsByChargeTypeQuery({
    skip:
      shouldSkip ||
      (isNotNil(providerFilter) && !providerFilter.includes(Provider.Azure)),
    variables: {
      query: query?.toString(),
      filter: {
        ...commonFilters,
        provider: [Provider.Azure],
        providerType: [providerType],
      },
    },
  });
  const { data: GKECost } = useCloudAccountCostsByChargeTypeQuery({
    skip:
      shouldSkip ||
      (isNotNil(providerFilter) && !providerFilter.includes(Provider.Gcp)),
    variables: {
      query: query?.toString(),
      filter: {
        ...commonFilters,
        provider: [Provider.Gcp],
        providerType: [providerType],
      },
    },
  });
  const { data: K8sOcCost } = useCloudAccountCostsByChargeTypeQuery({
    skip: shouldSkip,
    variables: {
      query: query?.toString(),
      filter: {
        ...commonFilters,
        provider: [Provider.Hybrid],
        providerType: [providerType],
      },
    },
  });

  const EKSTotalCost = getTotalCost(
    EKSCost?.cloudAccountCostsByChargeType?.costsByChargeType
  );
  const AKSTotalCost = getTotalCost(
    AKSCost?.cloudAccountCostsByChargeType?.costsByChargeType
  );
  const GKETotalCost = getTotalCost(
    GKECost?.cloudAccountCostsByChargeType?.costsByChargeType
  );
  const K8sOcTotalCost = getTotalCost(
    K8sOcCost?.cloudAccountCostsByChargeType?.costsByChargeType
  );

  return {
    eks: {
      cost: EKSTotalCost,
      icon: "aws",
      chargeType: Provider.Aws,
    },
    aks: {
      cost: AKSTotalCost,
      icon: "azure",
      chargeType: Provider.Azure,
    },
    gke: {
      cost: GKETotalCost,
      icon: "gcp",
      chargeType: Provider.Gcp,
    },
    k8sOc: {
      cost: K8sOcTotalCost,
      icon:
        providerType === CloudProviderType.Kubernetes
          ? "kubernetes"
          : "openshift",
      chargeType:
        providerType === CloudProviderType.Kubernetes
          ? Provider.Kubernetes
          : CloudProviderType.Openshift,
    },
  };
}
